import Alert from "@/components/Alert";
import { Button } from "@/components/Button";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Layout from "@/components/Layout";
import { Spinner } from "@/components/Spinner";
import { textAreaClass, textInputClass } from "@/components/Form";
import { useRouteParams } from "@/hooks/useEncodedIds";
import { BaseLivlyApiResponse } from "@/types/Base";
import { BASE_API_URL } from "@/utils/constants";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import moment from "moment-timezone";
import toLocalTime from "../utils/toLocalTime";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import useLivlyUser from "@/context/UserProvider";

type SurveyTargetingConfigResponse = {
  allResidents?: boolean;
  recipientGroupIds?: number[];
  unitGroupIds?: number[];
  targetedUserIds?: number[];
  filters?: Record<string, unknown> | null;
};

export interface SurveyDetails {
  surveyId?: number;
  title?: string;
  surveyDescription?: string;
  surveyType?: number | string;
  surveyTypeId?: number;
  surveyCategory?: number | string;
  surveyCategoryId?: number;
  surveyStatus?: number | string;
  surveyStatusId?: number;
  feedbackTypeId?: number | null;
  deliveryChannel?: number | string | null;
  deliveryChannelId?: number | null;
  surveyVersion?: number | null;
  completionWindowDays?: number | null;
  expiresAt?: string | null;
  expirationTimestamp?: string | null;
  triggerConfig?: string | null;
  targetingConfig?: SurveyTargetingConfigResponse | string | null;
  createdAt?: string | null;
  sent?: number | null;
  responseCount?: number | null;
  totalRecipients?: number | null;
  responseRate?: number | null;
  hasResponded?: boolean;
  questions?: Array<{
    surveyQuestionId?: number;
    questionType?: number | string;
    questionTypeId?: number;
    questionText?: string;
    isRequired?: boolean;
    displayOrder?: number;
    questionConfig?:
      | string
      | {
          minLabel?: string | null;
          maxLabel?: string | null;
          maxLength?: number | null;
        }
      | null;
    options?: Array<{
      surveyQuestionOptionId?: number;
      optionText?: string;
      displayOrder?: number;
      optionType?: string;
      optionTypeId?: number;
      optionTextInputValue?: string | null;
    }>;
    submittedAnswer?: {
      textValue?: string | null;
      numericValue?: number | null;
      dateValue?: string | null;
      selectedOptions?: Array<{
        optionId?: number;
        textValue?: string | null;
      }>;
    } | null;
  }>;
}

export type SurveyQuestion = NonNullable<SurveyDetails["questions"]>[number];
type SurveyOption = NonNullable<SurveyQuestion["options"]>[number];

export type SurveyAnswer = {
  optionIds?: number[];
  textValue?: string;
  scoreValue?: number;
  dateValue?: string;
  otherValues?: Record<number, string>;
};

const QUESTION_TYPE_ID = {
  SINGLE_SELECT: 0,
  MULTI_SELECT: 1,
  SHORT_TEXT: 2,
  LONG_TEXT: 3,
  NPS_SCORE: 4,
  YES_NO: 5,
  LIKERT_SCALE: 6,
  DATE_FIELD: 7
} as const;

function isQuestionType(
  question: SurveyQuestion | undefined,
  type:
    | "SingleSelect"
    | "MultiSelect"
    | "ShortText"
    | "LongText"
    | "NpsScore"
    | "YesNo"
    | "LikertScale"
    | "DateField"
) {
  if (!question) {
    return false;
  }

  // Existing web survey flow uses string question types. Keep that as primary behavior
  // and only use numeric type IDs as a compatibility fallback for external payloads.
  if (question.questionType === type) {
    return true;
  }

  switch (type) {
    case "YesNo":
      return (
        question.questionType === QUESTION_TYPE_ID.YES_NO ||
        question.questionTypeId === QUESTION_TYPE_ID.YES_NO
      );
    case "LikertScale":
      return (
        question.questionType === QUESTION_TYPE_ID.LIKERT_SCALE ||
        question.questionTypeId === QUESTION_TYPE_ID.LIKERT_SCALE
      );
    case "MultiSelect":
      return (
        question.questionType === QUESTION_TYPE_ID.MULTI_SELECT ||
        question.questionTypeId === QUESTION_TYPE_ID.MULTI_SELECT
      );
    case "ShortText":
      return (
        question.questionType === QUESTION_TYPE_ID.SHORT_TEXT ||
        question.questionTypeId === QUESTION_TYPE_ID.SHORT_TEXT
      );
    case "LongText":
      return (
        question.questionType === QUESTION_TYPE_ID.LONG_TEXT ||
        question.questionTypeId === QUESTION_TYPE_ID.LONG_TEXT
      );
    case "NpsScore":
      return (
        question.questionType === QUESTION_TYPE_ID.NPS_SCORE ||
        question.questionTypeId === QUESTION_TYPE_ID.NPS_SCORE
      );
    case "DateField":
      return (
        question.questionType === QUESTION_TYPE_ID.DATE_FIELD ||
        question.questionTypeId === QUESTION_TYPE_ID.DATE_FIELD
      );
    case "SingleSelect":
      return (
        question.questionType === QUESTION_TYPE_ID.SINGLE_SELECT ||
        question.questionTypeId === QUESTION_TYPE_ID.SINGLE_SELECT
      );
    default:
      return false;
  }
}

export type SurveyResponsePayload = {
  answers: Array<{
    surveyQuestionId: number;
    textValue?: string;
    numericValue?: number;
    dateValue?: string;
    selectedOptions?: Array<{
      optionId: number;
      textValue?: string;
    }>;
  }>;
};

const INPUT_OPTION_TYPE = "Input";

export function Track({ step, total }: { step: number; total: number }) {
  const percentage = Math.floor(((step + 1) / total) * 100);

  return (
    <div className="flex items-center gap-4 mt-4">
      <div className="flex items-center h-1 bg-gray-200 rounded-lg w-52">
        <div
          className="h-1 bg-[#F9877E] rounded-tl-lg rounded-bl-lg"
          style={{ width: `${percentage}%` }}
        />
      </div>
      {total > 1 && (
        <span className="text-sm text-gray-500">
          QUESTION {step + 1}/{total}
        </span>
      )}
    </div>
  );
}

function getQuestionConfig(question: SurveyQuestion | undefined): {
  minLabel?: string | null;
  maxLabel?: string | null;
  maxLength?: number | null;
} {
  if (
    !question ||
    !question.questionConfig ||
    typeof question.questionConfig === "string"
  ) {
    return {};
  }

  return question.questionConfig;
}

export function sortQuestions(questions: SurveyDetails["questions"]) {
  return [...(questions ?? [])].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
  );
}

function sortOptions(options: SurveyQuestion["options"]) {
  return [...(options ?? [])].sort(
    (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
  );
}

function isOtherOption(option: SurveyOption | undefined) {
  return option?.optionType === INPUT_OPTION_TYPE || option?.optionTypeId === 1;
}

export function isQuestionAnswered(
  question: SurveyQuestion | undefined,
  answer: SurveyAnswer | undefined
) {
  if (!question) {
    return false;
  }

  if (!question.isRequired) {
    return true;
  }

  const options = sortOptions(question.options);
  const selectedOptions = options.filter((option) =>
    answer?.optionIds?.includes(option.surveyQuestionOptionId ?? 0)
  );

  const hasOtherText = selectedOptions.every((option) => {
    if (!isOtherOption(option)) {
      return true;
    }

    const optionId = option.surveyQuestionOptionId ?? 0;
    return Boolean(answer?.otherValues?.[optionId]?.trim());
  });

  switch (true) {
    case isQuestionType(question, "SingleSelect"):
    case isQuestionType(question, "YesNo"):
    case isQuestionType(question, "LikertScale"):
      return Boolean(answer?.optionIds?.length) && hasOtherText;
    case isQuestionType(question, "MultiSelect"):
      return Boolean(answer?.optionIds?.length) && hasOtherText;
    case isQuestionType(question, "ShortText"):
    case isQuestionType(question, "LongText"):
      return Boolean(answer?.textValue?.trim());
    case isQuestionType(question, "NpsScore"):
      return typeof answer?.scoreValue === "number";
    case isQuestionType(question, "DateField"):
      return Boolean(answer?.dateValue);
    default:
      return false;
  }
}

function SurveyOptionControl({
  option,
  type,
  checked,
  onChange,
  otherValue,
  onChangeOther,
  disabled,
  name
}: {
  option: SurveyOption;
  type: "radio" | "checkbox";
  checked: boolean;
  onChange: () => void;
  otherValue?: string;
  onChangeOther: (value: string) => void;
  disabled?: boolean;
  name?: string;
}) {
  const optionId = option.surveyQuestionOptionId ?? 0;
  const hasOtherField = isOtherOption(option);

  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type={type}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className="h-4 w-4 text-[#F9877E] focus:ring-[#F9877E]"
          name={name ?? `question-option-${optionId}`}
        />
        <span className="text-sm text-gray-900">{option.optionText}</span>
      </label>

      {hasOtherField && checked && (
        <input
          type="text"
          value={otherValue ?? ""}
          onChange={(event) => onChangeOther(event.target.value)}
          placeholder="Type here"
          disabled={disabled}
          className={`${textInputClass} mt-3`}
        />
      )}
    </div>
  );
}

export function SurveyQuestionCard({
  question,
  answer,
  onAnswerChange,
  disabled,
  timezone
}: {
  question: SurveyQuestion;
  answer: SurveyAnswer | undefined;
  onAnswerChange: (next: SurveyAnswer) => void;
  disabled?: boolean;
  timezone?: string;
}) {
  const config = getQuestionConfig(question);
  const maxLength = config.maxLength ?? undefined;
  const options = sortOptions(question.options);
  const radioGroupName = `survey-question-${question.surveyQuestionId ?? "unknown"}`;

  if (
    isQuestionType(question, "SingleSelect") ||
    isQuestionType(question, "YesNo")
  ) {
    return (
      <div className="space-y-3">
        {options.map((option) => {
          const optionId = option.surveyQuestionOptionId ?? 0;
          return (
            <SurveyOptionControl
              key={optionId}
              option={option}
              type="radio"
              name={radioGroupName}
              checked={answer?.optionIds?.includes(optionId) ?? false}
              onChange={() =>
                onAnswerChange({
                  optionIds: [optionId],
                  otherValues: answer?.otherValues ?? {}
                })
              }
              disabled={disabled}
              otherValue={answer?.otherValues?.[optionId]}
              onChangeOther={(value) =>
                onAnswerChange({
                  optionIds: [optionId],
                  otherValues: {
                    ...(answer?.otherValues ?? {}),
                    [optionId]: value
                  }
                })
              }
            />
          );
        })}
      </div>
    );
  }

  if (isQuestionType(question, "MultiSelect")) {
    return (
      <div className="space-y-3">
        {options.map((option) => {
          const optionId = option.surveyQuestionOptionId ?? 0;
          const isChecked = answer?.optionIds?.includes(optionId) ?? false;

          return (
            <SurveyOptionControl
              key={optionId}
              option={option}
              type="checkbox"
              checked={isChecked}
              onChange={() => {
                if (disabled) {
                  return;
                }
                const optionIds = isChecked
                  ? (answer?.optionIds ?? []).filter((id) => id !== optionId)
                  : [...(answer?.optionIds ?? []), optionId];

                const otherValues = { ...(answer?.otherValues ?? {}) };
                if (isChecked) {
                  delete otherValues[optionId];
                }

                onAnswerChange({ optionIds, otherValues });
              }}
              disabled={disabled}
              otherValue={answer?.otherValues?.[optionId]}
              onChangeOther={(value) =>
                onAnswerChange({
                  optionIds: answer?.optionIds ?? [],
                  otherValues: {
                    ...(answer?.otherValues ?? {}),
                    [optionId]: value
                  }
                })
              }
            />
          );
        })}
      </div>
    );
  }

  if (isQuestionType(question, "ShortText")) {
    return (
      <div>
        <input
          type="text"
          value={answer?.textValue ?? ""}
          onChange={(event) =>
            onAnswerChange({ textValue: event.target.value })
          }
          placeholder="Enter your answer"
          maxLength={maxLength}
          disabled={disabled}
          className={textInputClass}
        />
        {maxLength ? (
          <p className="mt-2 text-sm text-gray-500">
            {maxLength - (answer?.textValue?.length ?? 0)} characters remaining
          </p>
        ) : null}
      </div>
    );
  }

  if (isQuestionType(question, "LongText")) {
    return (
      <div>
        <textarea
          rows={6}
          value={answer?.textValue ?? ""}
          onChange={(event) =>
            onAnswerChange({ textValue: event.target.value })
          }
          placeholder="Enter your answer"
          maxLength={maxLength}
          disabled={disabled}
          className={textAreaClass}
        />
        {maxLength ? (
          <p className="mt-2 text-sm text-gray-500">
            {maxLength - (answer?.textValue?.length ?? 0)} characters remaining
          </p>
        ) : null}
      </div>
    );
  }

  if (isQuestionType(question, "NpsScore")) {
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap gap-4 max-w-[44rem]">
          {Array.from({ length: 11 }, (_, score) => {
            const isSelected = answer?.scoreValue === score;

            return (
              <button
                key={score}
                type="button"
                onClick={() => onAnswerChange({ scoreValue: score })}
                disabled={disabled}
                className={`relative flex h-12 w-12 items-center justify-center rounded-full text-sm font-medium text-white transition ${
                  isSelected
                    ? "bg-[#FD8178] ring-4 ring-[#FD8178] ring-offset-4 ring-offset-white"
                    : "bg-[#A7A7A7] hover:bg-[#969696]"
                }`}
              >
                {score}
              </button>
            );
          })}
        </div>
        <div className="flex justify-between gap-4 text-sm text-gray-400">
          <span>0: {config.minLabel ?? "Not at all likely"}</span>
          <span>10: {config.maxLabel ?? "Extremely likely"}</span>
        </div>
      </div>
    );
  }

  if (isQuestionType(question, "LikertScale")) {
    return (
      <div className="space-y-3">
        {options.map((option) => {
          const optionId = option.surveyQuestionOptionId ?? 0;
          return (
            <SurveyOptionControl
              key={optionId}
              option={option}
              type="radio"
              name={radioGroupName}
              checked={answer?.optionIds?.includes(optionId) ?? false}
              onChange={() => onAnswerChange({ optionIds: [optionId] })}
              disabled={disabled}
              otherValue={answer?.otherValues?.[optionId]}
              onChangeOther={(value) =>
                onAnswerChange({
                  optionIds: [optionId],
                  otherValues: {
                    ...(answer?.otherValues ?? {}),
                    [optionId]: value
                  }
                })
              }
            />
          );
        })}
      </div>
    );
  }

  if (isQuestionType(question, "DateField")) {
    if (disabled) {
      return (
        <input
          type="text"
          value={
            answer?.dateValue
              ? toLocalTime(answer.dateValue, timezone).format("MM/DD/YYYY")
              : ""
          }
          disabled
          readOnly
          className="block rounded-md border-gray-300 shadow-sm focus:border-[#F9877E] focus:ring-[#F9877E] sm:text-sm"
        />
      );
    }

    return (
      <DatePicker
        // parse stored "YYYY-MM-DD" as local time — new Date(string) would parse as UTC and shift the day back
        selected={answer?.dateValue}
        onChange={(date: Date | null) =>
          onAnswerChange({
            dateValue: date?.toISOString()
          })
        }
        placeholderText="MM/DD/YYYY"
        dateFormat="MM/dd/yyyy"
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        yearDropdownItemNumber={100}
        scrollableYearDropdown
        className="block rounded-md border-gray-300 shadow-sm focus:border-[#F9877E] focus:ring-[#F9877E] sm:text-sm"
      />
    );
  }

  return null;
}

async function getSurveyDetails(leaseId: number, surveyId: number) {
  const result = await axios.get<BaseLivlyApiResponse<SurveyDetails>>(
    `${BASE_API_URL}/resident/surveys/${surveyId}?leaseId=${leaseId}`
  );

  return result.data.Data;
}

async function getSurveyResponseDetails(leaseId: number, surveyId: number) {
  const result = await axios.get<BaseLivlyApiResponse<SurveyDetails>>(
    `${BASE_API_URL}/resident/surveys/${surveyId}/response?leaseId=${leaseId}`
  );

  return result.data.Data;
}

export async function postSurveyResponse(
  leaseId: number,
  surveyId: number,
  payload: SurveyResponsePayload
) {
  await axios.post(
    `${BASE_API_URL}/resident/surveys/${surveyId}/response?leaseId=${leaseId}`,
    payload
  );
}

export function buildSurveyResponsePayload(
  questions: SurveyQuestion[],
  answers: Record<number, SurveyAnswer>,
  timezone?: string
): SurveyResponsePayload {
  const mappedAnswers = questions
    .map((question) => {
      const surveyQuestionId = question.surveyQuestionId ?? 0;
      const answer = answers[surveyQuestionId];

      if (!surveyQuestionId || !answer) {
        return null;
      }

      const selectedOptions = (answer.optionIds ?? []).map((optionId) => ({
        optionId,
        textValue: answer.otherValues?.[optionId]
      }));

      // use moment to interpret date string in local timezone (not UTC midnight)
      const dateValue = answer.dateValue?.split("T")[0] ?? undefined;

      const payloadAnswer = {
        surveyQuestionId,
        textValue: answer.textValue,
        numericValue: answer.scoreValue,
        dateValue,
        selectedOptions:
          selectedOptions.length > 0 ? selectedOptions : undefined
      };

      const hasValue =
        Boolean(payloadAnswer.textValue?.trim()) ||
        typeof payloadAnswer.numericValue === "number" ||
        Boolean(payloadAnswer.dateValue) ||
        Boolean(payloadAnswer.selectedOptions?.length);

      return hasValue ? payloadAnswer : null;
    })
    .filter((item): item is NonNullable<typeof item> => Boolean(item));

  return { answers: mappedAnswers };
}

function mapSubmittedAnswers(
  questions: SurveyQuestion[],
  timezone?: string
): Record<number, SurveyAnswer> {
  return questions.reduce(
    (acc, question) => {
      const questionId = question.surveyQuestionId ?? 0;
      const submittedAnswer = question.submittedAnswer;

      if (!questionId || !submittedAnswer) {
        return acc;
      }

      const optionIds = (submittedAnswer.selectedOptions ?? [])
        .map((option) => option.optionId)
        .filter((optionId): optionId is number => typeof optionId === "number");

      const otherValues = (submittedAnswer.selectedOptions ?? []).reduce<
        Record<number, string>
      >((optionAcc, option) => {
        if (
          typeof option.optionId === "number" &&
          typeof option.textValue === "string" &&
          option.textValue.length > 0
        ) {
          optionAcc[option.optionId] = option.textValue;
        }

        return optionAcc;
      }, {});

      acc[questionId] = {
        optionIds,
        textValue: submittedAnswer.textValue ?? undefined,
        scoreValue: submittedAnswer.numericValue ?? undefined,
        dateValue: submittedAnswer.dateValue
          ? toLocalTime(submittedAnswer.dateValue, timezone).format(
              "YYYY-MM-DD"
            )
          : undefined,
        otherValues
      };

      return acc;
    },
    {} as Record<number, SurveyAnswer>
  );
}

export function useSurveyDetails(
  leaseId?: number,
  surveyId?: number,
  mode: "survey" | "response" = "survey"
) {
  return useQuery({
    queryKey: ["survey", mode, "details", leaseId, surveyId],
    queryFn: () =>
      mode === "response"
        ? getSurveyResponseDetails(leaseId!, surveyId!)
        : getSurveyDetails(leaseId!, surveyId!),
    enabled: Number.isFinite(leaseId) && Number.isFinite(surveyId),
    refetchOnWindowFocus: false
  });
}

export default function SurveyPage() {
  const params = useRouteParams<{ leaseId: string; surveyId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const leaseId = Number(params.leaseId);
  const surveyId = Number(params.surveyId);
  const isResponseView = searchParams.get("view") === "response";
  // Callers pass ?from= so back navigation returns there instead of Home
  const from = searchParams.get("from");
  const backTarget =
    from === "surveys"
      ? { to: `/lease/${leaseId}/my-feedback/surveys`, label: "Surveys" }
      : { to: `/lease/${leaseId}/home`, label: "Home" };
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<number, SurveyAnswer>>({});
  const { user } = useLivlyUser();
  const timezone = user?.building?.timezone;

  const { data, isLoading, isError } = useSurveyDetails(
    leaseId,
    surveyId,
    isResponseView ? "response" : "survey"
  );

  const questions = useMemo(
    () => sortQuestions(data?.questions),
    [data?.questions]
  );
  const total = questions.length;
  const question = questions[step];
  const questionId = question?.surveyQuestionId ?? 0;
  const answer = answers[questionId];
  const isLastQuestion = step === total - 1;
  const isCurrentQuestionValid = isQuestionAnswered(question, answer);

  useEffect(() => {
    if (isResponseView && data?.questions) {
      setAnswers(mapSubmittedAnswers(sortQuestions(data.questions), timezone));
    }
  }, [isResponseView, data?.questions, timezone]);

  const submitResponseMutation = useMutation({
    mutationFn: (payload: SurveyResponsePayload) =>
      postSurveyResponse(leaseId, surveyId, payload),
    onSuccess: () => {
      navigate(`/lease/${leaseId}/surveys/${surveyId}/thank-you`);
    }
  });

  const onSetAnswer = (next: SurveyAnswer) => {
    if (!questionId) {
      return;
    }

    setAnswers((current) => ({
      ...current,
      [questionId]: next
    }));
  };

  const onSubmit = () => {
    if (isResponseView) {
      return;
    }

    if (submitResponseMutation.isLoading) {
      return;
    }

    const payload = buildSurveyResponsePayload(questions, answers);
    submitResponseMutation.mutate(payload);
  };

  if (isLoading) {
    return (
      <div className="h-full flex flex-1 justify-center items-center">
        <Spinner color="livly" size="xl" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="p-6">
        <Alert message="There was an error loading survey. Please try again." />
      </div>
    );
  }

  if (!question) {
    return (
      <Layout title={data.title ?? "Survey"} back={backTarget}>
        <div className="max-w-2xl">
          <Alert
            variant="default"
            message="This survey does not have any questions yet."
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={data.title ?? "Survey"} back={backTarget}>
      <div className="max-w-3xl pb-36">
        {total > 1 && <Track step={step} total={total} />}

        <div className="mt-6 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="border-b border-gray-100 px-5 py-4">
            <div className="mt-4 flex justify-between gap-3 text-sm text-gray-500">
              <span>{`${total} question${total === 1 ? "" : "s"}`}</span>
              {data.expiresAt ? (
                <span>
                  {" "}
                  Closes{" "}
                  {toLocalTime(data.expiresAt, timezone).format("MMM D, YYYY")}
                </span>
              ) : null}
            </div>
          </div>

          <div className="px-5 py-6">
            {submitResponseMutation.isError && (
              <div className="mb-4">
                <Alert message="Unable to submit survey right now. Please try again." />
              </div>
            )}
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500">
              Question {step + 1}/{total}
            </p>
            <div className="mt-3 flex items-start gap-2">
              <h2 className="text-lg font-normal leading-tight text-gray-900">
                {question.questionText}
              </h2>
              {question.isRequired ? (
                <span className="mt-1 text-sm font-medium text-[#F9877E]">
                  *
                </span>
              ) : null}
            </div>

            <div className="mt-6">
              <SurveyQuestionCard
                question={question}
                answer={answer}
                onAnswerChange={onSetAnswer}
                disabled={isResponseView}
                timezone={timezone}
              />
            </div>
          </div>
        </div>

        <div className="fixed bottom-0 left-0 right-0 flex flex-col items-stretch m-4 bg-white border border-gray-100 rounded-lg shadow-lg md:flex-row md:justify-between md:items-center md:left-64">
          <div className="flex flex-col items-center justify-end w-full gap-3 p-4 border-t border-gray-200 md:flex-row md:border-none">
            {step !== 0 && (
              <Button
                color="primary"
                outlined
                onClick={() => setStep((current) => current - 1)}
                className="w-full md:w-auto"
              >
                Previous
              </Button>
            )}

            {isResponseView ? (
              isLastQuestion ? (
                <Link
                  to={`/lease/${leaseId}/my-feedback/reviews`}
                  className="w-full md:w-auto"
                >
                  <Button color="secondary" className="w-full md:w-auto">
                    Done
                  </Button>
                </Link>
              ) : (
                <Button
                  color="secondary"
                  onClick={() => setStep((current) => current + 1)}
                  className="w-full md:w-auto"
                >
                  Next
                </Button>
              )
            ) : isLastQuestion ? (
              <Button
                color="secondary"
                disabled={
                  !isCurrentQuestionValid || submitResponseMutation.isLoading
                }
                onClick={onSubmit}
                className="w-full md:w-auto"
              >
                {submitResponseMutation.isLoading ? "Submitting..." : "Submit"}
              </Button>
            ) : (
              <Button
                color="secondary"
                onClick={() => setStep((current) => current + 1)}
                disabled={!isCurrentQuestionValid}
                className="w-full md:w-auto"
              >
                Next
              </Button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
